/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  Select,
  styled,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tooltipClasses,
  Typography,
} from "@mui/material";
import { globalColors } from "../../themes/colors";
import { LightTooltip } from "../statistics_cards/styles";

export const MODAL_TABLE_MAX_HEIGHT = 620;

// Column descriptions run long, so the header tooltip is wider than the shared LightTooltip.
export const ColumnInfoTooltip = styled(LightTooltip)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "min(480px, calc(100vw - 32px))",
  },
});

// Tables using this container pair it with a synced fake horizontal scrollbar, so its
// own scrollbars stay hidden to keep a second bar from rendering. Both properties are
// required: `scrollbar-width` is what Firefox and Chrome 121+ honour, the webkit rule
// covers older Blink. Removing either restores a duplicate bar.
export const StyledTableContainer = styled(TableContainer)({
  boxShadow: "none",
  overflowX: "auto",
  overflowY: "auto",
  height: "fit-content",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

export const StyledTableHead = styled(TableHead, {
  shouldForwardProp: (prop) => prop !== 'report'
})(({ report, evenRow }) => ({
  backgroundColor: evenRow ? '#ffffff' : report && "#f5f5f5",
}));

export const StyledTableHeadCell = styled(TableCell)({
  fontSize: "14px",
  fontWeight: 500,
  color: globalColors.tableHeader,
  padding: "5px 5px 5px 15px",
  height: "40px",
  position: "relative",
});

export const StyledTableBodyCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'report'
})(({ report }) => ({
  backgroundColor: report && "#f5f5f5",
  py: 1,
  fontSize: "14px",
  fontWeight: '400',
  color: globalColors.tableData,
  "&:last-child": { borderRight: 0 },
  whiteSpace: "nowrap",
  padding: "5px 5px 5px 15px",
  height: "40px",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

export const StyledTypography = styled(Typography)({
  color: globalColors.tableData,
  fontSize: "14px",
  fontWeight: '400',
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'report'
})(({ report, evenRow }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: evenRow ? '#ffffff' : !report && "#f7f7f7",
  },
}));

export const StyledPerPageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
});

export const StyledSelect = styled(Select)({
  backgroundColor: "white",
  width: 70,
  fontSize: "0.875rem",
  "& .MuiSelect-select": { py: "6px" },
  "&.MuiOutlinedInput-root": {
    height: "2.4rem",
    "&:hover fieldset": {
      borderColor: "#2b4fb9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2b4fb9",
    },
  },
});

export const StyledPaginationContainer = styled(Box)({
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  marginTop: "16px",
  marginBottom: "16px",
});

export const ExportContainer = styled('div')({
  display: 'flex', 
  gap: '8px', 
  alignItems: 'center'
})
